import gql from "graphql-tag";

export const HOME = gql`
  query getHome {
    me {
      id
      accountType
      firstName
      lastName
      providerName
    }
    assessments(options: { includeArchived: true }) {
      identifier
      name
      type
      isArchived
      categoryCriteria {
        order
        category
        clinicalCategory
        minScore
        maxScore
        color
      }
      subAssessments {
        identifier
        name
        type
        isArchived
        categoryCriteria {
          order
          category
          clinicalCategory
          minScore
          maxScore
          color
        }
      }
    }
    canReceiveMessages {
      canReceiveMessages
    }
    messageTemplates {
      id
      key
      name
      category
      message
    }
  }
`;

export const GET_CHILDREN = gql`
  query getChildren($excludeArchived: Boolean) {
    me {
      children(excludeArchived: $excludeArchived) {
        user {
          id
          accountType
          firstName
          lastName
          dateOfBirth
          pointsBalance
          createdAt
          username
          email
          lastActiveAt
          timeZone
          familyId
          ehrUserId
          activeClient
          groupName
          parents {
            user {
              id
              firstName
              lastName
              activeClient
              groupName
              ehrUserId
              email
            }
          }
        }
      }
    }
  }
`;

export const THERAPIST_GET_CUSTOM_GOALS = gql`
  query getTherapistCustomGoals {
    therapistCustomGoals {
      id
      name
      value
      imageId
    }
  }
`;
export const THERAPIST_GET_CUSTOM_PARENT_GOALS = gql`
  query getTherapistCustomParentGoals {
    goals(options: { includeAssignable: true, assigneeTypes: ["PARENT"] }) {
      id
      name
      description
      dueAt
      assignedAt
      reminderTime1
      reminderTime2
    }
  }
`;

export const THERAPIST_GET_MOOD = gql`
  query getChildMood($childId: Int!, $daysBack: Int) {
    childMood(childId: $childId, daysBack: $daysBack) {
      sadCount
      neutralCount
      happyCount
    }
  }
`;

export const THERAPIST_GET_CHILD = gql`
  query getChildDetails($childId: Int!) {
    childDetails(childId: $childId) {
      id
      accountType
      firstName
      lastName
      lifetimePoints
      createdAt
      therapists {
        user {
          id
        }
      }
    }
    childParents(childId: $childId) {
      id
      firstName
      lastName
      email
      phoneNumber
      createdAt
      lastActiveAt
      timeZone
      accountType
      ehrUserId
      goals {
        id
        name
        description
        dueAt
        assignedAt
        reminderTime1
        reminderTime2
        achievements(options: { excludePending: true }) {
          id
          achievedAt
        }
      }
      achievements(options: { excludePending: true }) {
        id
        achievedAt
        goalFeedback
        feelingDuringCompletion
        feedbackText
        goal {
          id
          name
        }
      }
    }
    childGoals(childId: $childId, options: { includeFinalized: true }) {
      id
      name
      dueAt
      dailyFrequency
      expiresAt
      finalizedAt
      value
      assignedAt
      assignerType
      reminderTime1
      achievements(childId: $childId, options: { excludePending: true }) {
        id
        achievedAt
        points
        image
      }
    }
    achievements(childId: $childId, options: { excludePending: true }) {
      id
      achievedAt
      points
      goalFeedback
      feelingDuringCompletion
      feedbackText
      goal {
        id
        name
        dueAt
        assignedAt
        expiresAt
      }
    }
    rewards(childId: $childId) {
      id
      name
      redemptions {
        approvedBy {
          id
        }
        redeemedAt
        cost
      }
    }
  }
`;

export const GET_ASSESSMENT_ASSIGNMENTS_AND_COMPLETIONS = gql`
  query getAssessmentAssignmentsAndCompletions($userIds: [Int], $assessmentIdentifiers: [String]) {
    assessmentsAssignable(userIds: $userIds) {
      assessmentIdentifiers
      assignable
      userAssessments {
        userId
        assessmentIdentifiers
      }
    }
    assessmentAssignments(assigneeIds: $userIds) {
      assigneeId
      assessmentIdentifier
      assessmentParameters {
        subjectUserId
      }
    }
    completedAssessments(options: { userIds: $userIds, assessmentIdentifiers: $assessmentIdentifiers }) {
      id
      clientId
      assessmentIdentifier
      category
      score
      scorePossible
      completedAt
      name
      isArchived
      answers {
        questionId
        choiceId
        questionText
        choiceText
        responseText
      }
      assessmentParameters {
        subjectUserId
      }
      subResponses {
        id
        clientId
        assessmentIdentifier
        category
        score
        scorePossible
        completedAt
        name
        isArchived
        answers {
          questionId
          choiceId
          questionText
          choiceText
        }
        assessmentParameters {
          subjectUserId
        }
      }
    }
  }
`;

export const ASSIGN_ASSESSMENTS = gql`
  mutation assignAssessments($assignments: [AssessmentAssignmentRequest!]) {
    assignAssessments(assignments: $assignments) {
      assigneeId
    }
  }
`;

export const ADULT_GET_REWARDS = gql`
  query getAdultRewards($childId: Int!) {
    childDetails(childId: $childId) {
      id
      firstName
    }
    rewards(childId: $childId) {
      id
      name
      description
      imageId
      cost
      assignedTo {
        id
      }
    }
  }
`;

export const ADULT_GET_GOALS = gql`
  query getAdultGoals($childId: Int!) {
    childDetails(childId: $childId) {
      id
      accountType
      firstName
      theme
    }
    adultGoals(childId: $childId) {
      id
      name
      description
      dueAt
      dailyFrequency
      reminderTime1
      reminderTime2
      expiresAt
      value
      imageId
      assignedTo {
        id
      }
      readOnly
      assignerType
    }
  }
`;

export const GET_GOAL_ACHIEVEMENT_IMAGE = gql`
  query getGoalAchievementImage($assigneeId: Int!, $goalId: Int!, $achievementId: Int!) {
    achievementImage(assigneeId: $assigneeId, goalId: $goalId, achievementId: $achievementId) {
      image
    }
  }
`;

export const THERAPIST_CREATE_GOAL = gql`
  mutation createGoal(
    $assignedTo: Int!
    $name: String!
    $description: String
    $dueAt: String!
    $dailyFrequency: Int
    $reminderTime1: String
    $reminderTime2: String
    $expiresAt: String!
    $value: Int!
    $imageId: Int!
    $createUnassignedCopy: Boolean
    $unassignedCopyReminderTime1: String
    $unassignedCopyReminderTime2: String
  ) {
    createGoal(
      name: $name
      description: $description
      dueAt: $dueAt
      dailyFrequency: $dailyFrequency
      reminderTime1: $reminderTime1
      reminderTime2: $reminderTime2
      expiresAt: $expiresAt
      assignedTo: $assignedTo
      value: $value
      imageId: $imageId
      createUnassignedCopy: $createUnassignedCopy
      unassignedCopyReminderTime1: $unassignedCopyReminderTime1
      unassignedCopyReminderTime2: $unassignedCopyReminderTime2
    ) {
      id
    }
  }
`;

export const THERAPIST_UPDATE_GOAL = gql`
  mutation updateGoal(
    $goalId: Int!
    $name: String!
    $description: String
    $dueAt: String!
    $dailyFrequency: Int
    $reminderTime1: String
    $reminderTime2: String
    $value: Int!
    $imageId: Int!
  ) {
    updateGoal(
      goalId: $goalId
      name: $name
      description: $description
      dueAt: $dueAt
      dailyFrequency: $dailyFrequency
      reminderTime1: $reminderTime1
      reminderTime2: $reminderTime2
      value: $value
      imageId: $imageId
    ) {
      id
    }
  }
`;

export const DELETE_GOAL = gql`
  mutation deleteGoal($goalId: Int!) {
    deleteGoal(goalId: $goalId)
  }
`;

export const RENEW_GOAL = gql`
  mutation renewGoal($goalId: Int!, $childId: Int!, $newExpirationDate: String!) {
    renewGoal(goalId: $goalId, childId: $childId, newExpirationDate: $newExpirationDate) {
      id
      expiresAt
    }
  }
`;

export const FINALIZE_GOAL = gql`
  mutation finalizeGoal($goalId: Int!, $childId: Int!) {
    finalizeGoal(childId: $childId, goalId: $goalId) {
      id
      finalizedAt
    }
  }
`;

export const COMPLETE_AND_VERIFY_GOAL = gql`
  mutation completeAndVerifyGoal($goalId: Int!, $childId: Int!, $note: String, $achievedDate: String) {
    completeAndVerifyGoal(goalId: $goalId, childId: $childId, note: $note, achievedDate: $achievedDate) {
      id
    }
  }
`;

export const THERAPIST_INVITE_USER = gql`
  mutation inviteUser(
    $email: String!
    $note: String
    $language: String!
    $accountType: String!
    $clientName: String!
    $primaryTherapistId: Int
  ) {
    therapistInviteUser(
      email: $email
      note: $note
      language: $language
      accountType: $accountType
      clientName: $clientName
      primaryTherapistId: $primaryTherapistId
    )
  }
`;

export const THERAPIST_RESEND_INVITE = gql`
  mutation resendInvite($inviteId: Int!, $note: String) {
    therapistResendInviteUser(inviteId: $inviteId, note: $note)
  }
`;

export const THERAPIST_DELETE_INVITE = gql`
  mutation deleteInvite($inviteId: Int!) {
    therapistDeleteInviteUser(inviteId: $inviteId)
  }
`;

export const THERAPIST_GET_INVITES = gql`
  query getTherapistInvites {
    therapistInvites {
      inviteId
      email
      accountType
      clientName
    }
  }
`;

export const STORE_PENDING_INVITE_GOALS = gql`
  mutation storePendingInviteGoals($inviteId: Int!, $goals: [PendingGoal], $timeZoneOffset: String!) {
    storePendingInviteGoals(inviteId: $inviteId, goals: $goals, timeZoneOffset: $timeZoneOffset)
  }
`;

export const THERAPIST_LINK_EXISTING = gql`
  mutation linkExistingFamily($parentEmail: String!, $language: String!, $primaryTherapistId: Int) {
    therapistLinkExistingFamily(
      parentEmail: $parentEmail
      language: $language
      primaryTherapistId: $primaryTherapistId
    ) {
      id
      firstName
    }
  }
`;

export const SEND_MESSAGE = gql`
  mutation sendMessage($options: SendMessageOptions!) {
    sendMessage(options: $options)
  }
`;

export const GET_CONVERSATIONS = gql`
  query getConversations($options: ConversationQueryOptions!) {
    conversations(options: $options) {
      id
      read
      users {
        id
        firstName
        lastName
        accountType
        familyId
      }
      messages {
        id
        from {
          id
          firstName
          lastName
        }
        message
        date
        automated
        hasAttachments
      }
      recipientReadAt
      type
      familyId
      archivedAt
      pendingActions {
        id
        reason
      }
      permissions
    }
  }
`;

export const MESSAGE_ATTACHMENTS = gql`
  query getMessageAttachments($messageId: Int!, $includeData: Boolean, $asUserId: Int) {
    messageAttachments(messageId: $messageId, includeData: $includeData, asUserId: $asUserId) {
      name
      mimeType
      base64
    }
  }
`;

export const MARK_CONVERSATION_READ = gql`
  mutation markConversationRead($conversationId: Int!) {
    markConversationRead(conversationId: $conversationId)
  }
`;

export const GET_ALL_THERAPIST_CHILD_PARENTS = gql`
  query getTherapistChildParents {
    therapistChildParents {
      adultId
      childId
    }
  }
`;

export const THERAPIST_GET_CARE_TEAM = gql`
  query getTherapistCareTeam {
    careTeam {
      careCoordinationEnabled
      users {
        id
        firstName
        lastName
        accountType
        email
        ehrUserId
        credentialing
        licensedStates
        profileImageUrl
        createdAt
        lastActiveAt
      }
    }
  }
`;

export const SEND_MESSAGE_BATCH = gql`
  mutation sendMessageBatch($message: String, $options: [ConversationOptions]) {
    sendMessageBatch(message: $message, options: $options)
  }
`;

export const GET_ACTIVITY_FEED = gql`
  query getActivityFeed($userIds: [Int]!, $offset: Int, $limit: Int) {
    activityFeed(userIds: $userIds, offset: $offset, limit: $limit) {
      date
      title
      subtitle
      icon
      image
    }
  }
`;

export const CREATE_CONVERSATION = gql`
  mutation createConversation($userIds: [Int]!) {
    createConversation(userIds: $userIds)
  }
`;

export const THERAPIST_ADD_CONNECTION = gql`
  mutation addConnectionToCareTeamMember($connectUserEmail: String!, $connectToCareMemberUserId: Int!) {
    addConnectionToCareTeamMember(
      connectToCareMemberUserId: $connectToCareMemberUserId
      connectUserEmail: $connectUserEmail
      linkUninvitedUser: true
    ) {
      status
      message
    }
  }
`;

export const DELETE_PENDING_ACTIONS = gql`
  mutation deletePendingActions($conversationIds: [Int]!) {
    deletePendingActions(conversationIds: $conversationIds)
  }
`;

export const GET_FAMILIES = gql`
  query getFamilies($ids: [Int]!) {
    families(ids: $ids) {
      id
      users {
        id
        firstName
        lastName
        accountType
        email
        phoneNumber
        dateOfBirth
        ehrUserId
        groupName
        providerName
      }
    }
  }
`;

export const LEAVE_CONVERSATION = gql`
  mutation leaveConversation($conversationId: Int) {
    leaveConversation(conversationId: $conversationId)
  }
`;

export const GET_UNREAD_CONVERSATION_COUNT = gql`
  query getUnreadConversationCount {
    unreadConversationCount
  }
`;

export const GET_CHILDREN_BY_PARENT_EMAIL = gql`
  query getChildrenByParentEmail($parentEmail: String!) {
    childrenByParentEmail(parentEmail: $parentEmail) {
      id
      firstName
      lastName
      therapists {
        user {
          id
          firstName
          lastName
        }
      }
      parentCoaches {
        user {
          id
          firstName
          lastName
        }
      }
      careCoordinators {
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const REMOVE_CONNECTION = gql`
  mutation removeConnectionToCareTeamMember(
    $disconnectCareMemberUserId: Int
    $disconnectAllCareTeamMembers: Boolean
    $disconnectUserId: Int!
  ) {
    removeConnectionToCareTeamMember(
      disconnectCareMemberUserId: $disconnectCareMemberUserId
      disconnectAllCareTeamMembers: $disconnectAllCareTeamMembers
      disconnectUserId: $disconnectUserId
    )
  }
`;

export const DELETE_MESSAGE = gql`
  mutation deleteMessage($messageId: Int!) {
    deleteMessage(messageId: $messageId)
  }
`;

export const CREATE_APPOINTMENT = gql`
  mutation createAppointment(
    $appointmentTypeId: String!
    $attendeeIds: [Int!]!
    $date: String!
    $contactMethod: String!
  ) {
    createAppointment(
      appointmentTypeId: $appointmentTypeId
      attendeeIds: $attendeeIds
      date: $date
      contactMethod: $contactMethod
    ) {
      id
      date
      length
      contactMethod
      attendees {
        id
        firstName
        lastName
        familyId
        accountType
      }
      appointmentType {
        id
        name
      }
    }
  }
`;

export const GET_APPOINTMENT_TYPES = gql`
  query getAppointmentTypes {
    appointmentTypes {
      id
      name
      length
      isGroup
      contactMethods
      applicableCareTeamMemberIds
      pricingInfo {
        priceInCents
      }
    }
  }
`;

export const GET_AVAILABLE_DAYS = gql`
  query getAvailableDays(
    $dateInMonth: String!
    $timeZone: String
    $userIds: [Int]!
    $appointmentTypeId: String!
    $contactMethod: String!
    $recurringInterval: RecurringAppointmentInterval
    $recurringCount: Int
  ) {
    availableDays(
      dateInMonth: $dateInMonth
      timeZone: $timeZone
      userIds: $userIds
      appointmentTypeId: $appointmentTypeId
      contactMethod: $contactMethod
      recurringInterval: $recurringInterval
      recurringCount: $recurringCount
    ) {
      availableDates
    }
  }
`;

export const GET_AVAILABLE_TIME_SLOTS = gql`
  query getAvailableTimeSlots(
    $startDate: String!
    $endDate: String!
    $timeZone: String
    $userIds: [Int]!
    $appointmentTypeId: String!
    $contactMethod: String!
    $recurringInterval: RecurringAppointmentInterval
    $recurringCount: Int
  ) {
    availableTimeSlots(
      startDate: $startDate
      endDate: $endDate
      timeZone: $timeZone
      userIds: $userIds
      appointmentTypeId: $appointmentTypeId
      contactMethod: $contactMethod
      recurringInterval: $recurringInterval
      recurringCount: $recurringCount
    ) {
      userId
      startTime
    }
  }
`;

export const GET_USER = gql`
  query getUser {
    me {
      id
      accountType
      firstName
      lastName
      email
      providerName
      ehrUserId
    }
  }
`;

export const GET_FAMILY_MEMBERS = gql`
  query getFamilyMembers {
    me {
      id
      accountType
      firstName
      lastName
      email
      dateOfBirth
    }
    familyMembers {
      id
      firstName
      lastName
      accountType
      dateOfBirth
    }
  }
`;

export const GET_FORMS = gql`
  query getForms($userIds: [Int!], $ids: [String!], $status: FormStatus) {
    forms(userIds: $userIds, ids: $ids, status: $status) {
      id
      user {
        id
        firstName
      }
      formTemplate {
        id
        name
      }
      status
    }
  }
`;

export const CREATE_EHR_API_KEYS = gql`
  mutation createEhrApiKeys($userIds: [Int!]) {
    createEhrApiKeys(userIds: $userIds) {
      apiKey
      userId
      ehrUserId
    }
  }
`;

export const GET_GUIDING_STARS = gql`
  query getGuidingStars($userIds: [Int!]!) {
    guidingStars(userIds: $userIds) {
      id
      userId
      description
    }
  }
`;

export const GET_CARE_TEAM_MATCHES = gql`
  query getCareTeamMemberMatches($userIds: [Int!]!) {
    careTeamMemberMatches(userIds: $userIds) {
      user {
        id
        firstName
        lastName
        accountType
        familyId
      }
      careTeamMember {
        id
        firstName
        lastName
        accountType
        credentialing
        profileImageUrl
      }
    }
  }
`;

export const CAREGIVER_GET_CARE_TEAM_MATCHES = gql`
  query getFamilyMemberCareTeamMatches($userIds: [Int!]!) {
    familyMembers {
      id
      firstName
      lastName
      accountType
    }
    careTeamMemberMatches(userIds: $userIds) {
      user {
        id
        firstName
        lastName
        accountType
        familyId
      }
      careTeamMember {
        id
        firstName
        lastName
        accountType
        credentialing
        profileImageUrl
      }
    }
  }
`;

export const GET_CARE_SCREENERS = gql`
  query getCareScreeners($userId: Int!, $filterByLatest: Boolean) {
    careScreeners(userId: $userId, filterByLatest: $filterByLatest) {
      id
      typeformId
      typeformResponseId
      path
      email
      phoneNumber
      schedulingQueryStartDate
      createdAt
    }
  }
`;

export const SEND_CARE_TEAM_MEMBER_MATCHES_EMAIL = gql`
  mutation sendCareTeamMemberMatchesEmail($contextUserId: Int!, $emailRecipientUserIds: [Int!]!) {
    sendCareTeamMemberMatchesEmail(contextUserId: $contextUserId, emailRecipientUserIds: $emailRecipientUserIds) {
      operationName
      mutatedObjects {
        id
        data
      }
      errors
    }
  }
`;

export const SET_CARE_TEAM_MEMBER_MATCHES = gql`
  mutation setCareTeamMemberMatches(
    $userId: Int!
    $careTeamMemberIds: [Int!]!
    $retainCareCoordinatorMatches: Boolean
  ) {
    setCareTeamMemberMatches(
      userId: $userId
      careTeamMemberIds: $careTeamMemberIds
      retainCareCoordinatorMatches: $retainCareCoordinatorMatches
    ) {
      user {
        id
        firstName
        lastName
        accountType
        familyId
      }
      careTeamMember {
        id
        firstName
        lastName
        accountType
      }
    }
  }
`;

export const CREATE_INSURANCE_CARD = gql`
  mutation createInsuranceCard($userIds: [Int!]!, $frontImage: FileInput!, $backImage: FileInput!) {
    createInsuranceCard(userIds: $userIds, frontImage: $frontImage, backImage: $backImage) {
      id
      userIds
      createdByUserId
      createdAt
      frontImage {
        name
        mimeType
        path
      }
      backImage {
        name
        mimeType
        path
      }
    }
  }
`;

export const SEARCH_EXTERNAL_ORGANIZATIONS = gql`
  query searchExternalOrganizations($searchText: String!) {
    searchExternalOrganizations(searchText: $searchText) {
      id
      name
    }
  }
`;

export const GET_EXTERNAL_ORGANIZATIONS = gql`
  query getExternalOrganizations($ids: [Int!]!) {
    externalOrganizations(ids: $ids, limit: 10, offset: 0) {
      id
      name
    }
  }
`;

export const SEARCH_EXTERNAL_PROVIDERS = gql`
  query searchExternalProviders($searchText: String!, $externalOrganizationId: Int!) {
    searchExternalProviders(searchText: $searchText, externalOrganizationId: $externalOrganizationId) {
      id
      contact {
        firstName
        lastName
        fullName
      }
    }
  }
`;

export const CREATE_EXTERNAL_ORGANIZATION = gql`
  mutation createExternalOrganization($input: CreateExternalOrganizationInput!) {
    createExternalOrganization(input: $input) {
      id
      name
    }
  }
`;

export const CREATE_EXTERNAL_PROVIDER = gql`
  mutation createExternalProvider($input: CreateExternalProviderInput!) {
    createExternalProvider(input: $input) {
      id
      contact {
        firstName
        lastName
        fullName
      }
    }
  }
`;

export const UPDATE_EXTERNAL_PROVIDER = gql`
  mutation updateExternalProvider($id: Int!, $input: UpdateExternalProviderInput!) {
    updateExternalProvider(id: $id, input: $input) {
      id
      receiveUpdates
    }
  }
`;

export const GET_REFERRALS = gql`
  query getReferrals($limit: Int, $offset: Int, $externalProviderIds: [Int!]) {
    referrals(limit: $limit, offset: $offset, externalProviderIds: $externalProviderIds) {
      id
      createdAt
      note
      status
      externalProvider {
        id
        contact {
          firstName
          lastName
          fullName
          email
          phoneNumber
          faxNumber
          businessName
        }
      }
      externalOrganizationId
      externalOrganization {
        id
        name
        stateCode
        partnerType
        utmSource
        utmMedium
        utmCampaign
      }
      referredClients {
        id
        type
        contact {
          firstName
          lastName
          fullName
          email
          phoneNumber
          dob
        }
        isCareRecipient
        serviceLine
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_CLIENT_EXTERNAL_PREVIEWS = gql`
  query getClientExternalPreviews($externalProviderIds: [Int!]) {
    clientExternalPreviews(externalProviderIds: $externalProviderIds) {
      firstName
      lastName
      dateAdded
      status
      details
      hasProgressSummary
      progressSummaryUserId
    }
  }
`;

export const GET_REFERRING_PROVIDER_STATS = gql`
  query getReferringProviderStats($externalProviderId: Int!) {
    referringProviderStats(externalProviderId: $externalProviderId) {
      referralsMade
      therapySessionsCompleted
    }
  }
`;

export const GET_CLIENT_PROGRESS_SUMMARY = gql`
  query getClientProgressSummary($userId: Int!) {
    clientProgressSummary(userId: $userId) {
      summaryDate
      intakeDate
      intakeConcerns
      clientDob
      referringProvider
      diagnosis
      childGoals
      familyGoals
      sessionsCompleted
      sessionsShowRate
    }
  }
`;

export const CREATE_REFERRAL = gql`
  mutation createReferral($input: CreateReferralInput!) {
    createReferral(input: $input) {
      id
    }
  }
`;

export const GET_EXTERNAL_PROVIDERS = gql`
  query getExternalProviders($ids: [Int!], $limit: Int, $offset: Int, $searchText: String) {
    getExternalProviders(ids: $ids, limit: $limit, offset: $offset, searchText: $searchText) {
      id
      contact {
        firstName
        lastName
        fullName
        email
        phoneNumber
        businessName
      }
      externalOrganizationId
      externalOrganization {
        id
        name
        stateCode
        partnerType
        canReceiveUpdates
      }
      receiveUpdates
      createdAt
      updatedAt
    }
  }
`;

export const GET_EXTERNAL_ORGANIZATIONS_LIST = gql`
  query getExternalOrganizationsList($limit: Int!, $offset: Int!, $searchText: String) {
    externalOrganizations(limit: $limit, offset: $offset, searchText: $searchText) {
      id
      name
      stateCode
      partnerType
      canReceiveUpdates
      createdAt
      updatedAt
    }
  }
`;

export const SEARCH_USERS = gql`
  query searchUsers($searchText: String!) {
    users(searchText: $searchText) {
      id
      firstName
      lastName
      accountType
      email
      phoneNumber
      dateOfBirth
      ehrUserId
      groupName
      familyId
      providerName
    }
  }
`;

export const SEARCH_CARE_SCREENERS = gql`
  query searchCareScreeners($searchText: String!) {
    careScreeners(searchText: $searchText) {
      id
      email
      phoneNumber
      path
      consultStatus
      directIntakeStatus
      createdAt
      userId
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

export const SEARCH_EHR_USERS = gql`
  query searchEhrUsers($searchText: String!) {
    ehrUsers(searchText: $searchText) {
      id
      manateeUserId
      active
      firstName
      lastName
      email
      phoneNumber
      groupName
    }
  }
`;

export const SEARCH_REFERRALS = gql`
  query searchReferrals($searchText: String!) {
    referrals(searchText: $searchText) {
      id
      status
      createdAt
      externalProvider {
        id
        contact {
          firstName
          lastName
          fullName
          email
          phoneNumber
          businessName
        }
      }
      referredClients {
        id
        type
        userId
        contact {
          firstName
          lastName
          fullName
          email
          phoneNumber
        }
        isCareRecipient
      }
    }
  }
`;

export const CREATE_ONE_TIME_AUTH_LINK = gql`
  mutation createOneTimeAuthLink($externalProviderId: Int!, $sendEmail: Boolean) {
    createOneTimeAuthLink(externalProviderId: $externalProviderId, sendEmail: $sendEmail) {
      link
      message
      success
    }
  }
`;

export const GET_APPOINTMENTS = gql`
  query getAppointments($userIds: [Int!], $startDate: String!, $endDate: String!) {
    appointments(userIds: $userIds, startDate: $startDate, endDate: $endDate) {
      id
      date
      length
      contactMethod
      telehealthJoinUrl
      pmStatus
      attendees {
        id
        firstName
        lastName
        accountType
      }
      appointmentType {
        id
        name
      }
    }
  }
`;

export const GET_EXTERNAL_ORGANIZATION_STATS = gql`
  query getExternalOrganizationStats($providerLimit: Int, $providerOffset: Int) {
    externalOrganizations(limit: 10, offset: 0, providerLimit: $providerLimit, providerOffset: $providerOffset) {
      id
      name
      clientStats {
        timeframe
        referredClientCount
        activeClientCount
        totalClientCount
      }
      providers {
        id
        contact {
          fullName
        }
        clientStats {
          timeframe
          referredClientCount
          activeClientCount
          totalClientCount
        }
        lastReferralDate
      }
      totalProviderCount
      currentProviderId
    }
  }
`;

export const GET_AUTHENTICATED_PROVIDER = gql`
  query getAuthenticatedProvider {
    externalOrganizations(limit: 10, offset: 0, providerLimit: 100, providerOffset: 0) {
      id
      name
      currentProviderId
      totalProviderCount
      providers {
        id
        contact {
          firstName
          lastName
          fullName
        }
      }
    }
  }
`;
